import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BellRing, Pause, Play, Timer, X } from 'lucide-react';
import { cx } from '../lib/cx';
import {
  collectFinished,
  formatClock,
  notify,
  pauseTimer,
  playAlarm,
  remainingMs,
  removeTimer,
  resumeTimer,
  useTimers,
} from '../lib/timers';

/** Floating list of cooking timers, visible on every page. Rings when one finishes. */
export function TimerTray() {
  const { t } = useTranslation();
  const timers = useTimers((s) => s.timers);
  const [now, setNow] = useState(() => Date.now());
  const running = timers.some((timer) => timer.status === 'running');
  const ringing = timers.some((timer) => timer.status === 'done');
  const tray = useRef<HTMLElement>(null);

  // Publish the tray height so other floating buttons can sit above it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = tray.current;
    if (!el) {
      root.style.removeProperty('--timer-tray-h');
      return;
    }
    const observer = new ResizeObserver(() => root.style.setProperty('--timer-tray-h', `${el.offsetHeight + 12}px`));
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--timer-tray-h');
    };
  }, [timers.length > 0]);

  useEffect(() => {
    if (!running) return;
    const tick = () => {
      setNow(Date.now());
      for (const timer of collectFinished()) {
        void notify(t('timers.notificationTitle'), `${timer.label} · ${timer.recipeName}`, `${timer.recipePath}?step=${timer.stepIndex + 1}`, timer.id);
        navigator.vibrate?.([400, 200, 400, 200, 400]);
      }
    };
    tick();
    const interval = window.setInterval(tick, 250);
    return () => window.clearInterval(interval);
  }, [running, t]);

  useEffect(() => {
    if (!ringing) return;
    playAlarm();
    const interval = window.setInterval(playAlarm, 2000);
    const stop = window.setTimeout(() => window.clearInterval(interval), 60_000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stop);
    };
  }, [ringing]);

  if (!timers.length) return null;

  return (
    <section ref={tray} aria-label={t('timers.title')} className="fixed start-3 bottom-3 z-40 flex w-72 max-w-[calc(100vw-1.5rem)] flex-col gap-2">
      {timers.map((timer) => {
        const done = timer.status === 'done';
        const left = remainingMs(timer, now);
        const progress = done ? 1 : 1 - left / timer.durationMs;
        return (
          <div
            key={timer.id}
            className={cx(
              'animate-pop-in overflow-hidden rounded-2xl border shadow-lg',
              done ? 'border-brand bg-brand text-on-brand' : 'border-line bg-surface',
            )}
            role={done ? 'alert' : undefined}
          >
            <div className="flex items-center gap-3 px-3 py-2.5">
              {done ? <BellRing className="animate-ring size-6 shrink-0" /> : <Timer className="size-6 shrink-0 text-brand" />}
              <Link to={`${timer.recipePath}?step=${timer.stepIndex + 1}`} className="min-w-0 flex-1" title={t('timers.goToStep')}>
                <p className="truncate text-sm font-semibold">{timer.label}</p>
                <p className={cx('truncate text-xs', done ? 'opacity-90' : 'text-muted')}>{timer.recipeName}</p>
              </Link>
              <span className={cx('text-lg font-bold', !done && 'font-mono tabular-nums')}>{done ? t('timers.done') : formatClock(left)}</span>
              {!done &&
                (timer.status === 'running' ? (
                  <button type="button" className="icon-btn size-8" onClick={() => pauseTimer(timer.id)} aria-label={t('timers.pause')}>
                    <Pause className="size-4" />
                  </button>
                ) : (
                  <button type="button" className="icon-btn size-8" onClick={() => resumeTimer(timer.id)} aria-label={t('timers.resume')}>
                    <Play className="size-4" />
                  </button>
                ))}
              <button
                type="button"
                className={cx('icon-btn size-8', done && 'text-on-brand hover:bg-white/20')}
                onClick={() => removeTimer(timer.id)}
                aria-label={done ? t('timers.dismiss') : t('timers.cancel')}
              >
                <X className="size-4" />
              </button>
            </div>
            {!done && (
              <div className="h-1 bg-surface-2">
                <div className="h-full bg-brand transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
